import React, { useState } from 'react';
import { Typography, Row, Col, Card, Select, Button, Space, Tabs, Table, Tag, Tooltip, Dropdown, Menu } from 'antd';
import {
    FilterOutlined,
    DownloadOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    MessageOutlined,
    HistoryOutlined,
    FullscreenOutlined,
    MoreOutlined,
    EditOutlined,
    ExportOutlined,
    ScheduleOutlined,
    ShareAltOutlined,
    SettingOutlined,
    DeleteOutlined,
    SwapOutlined,
    HomeOutlined,
    CloudDownloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { colors, palette } from '../theme/themeConfig';

const { Title, Text } = Typography;
const { Option } = Select;

// Toolbar Action Menu
const actionMenu = (
    <Menu>
        <Menu.Item key="edit" icon={<EditOutlined />}>Edit</Menu.Item>
        <Menu.Item key="landing" icon={<HomeOutlined />}>Set as Landing Page</Menu.Item>
        <Menu.Item key="rename" icon={<EditOutlined />}>Rename</Menu.Item>
        <Menu.Item key="export" icon={<ExportOutlined />}>Export</Menu.Item>
        <Menu.Item key="scheduler" icon={<ScheduleOutlined />}>Create Scheduler</Menu.Item>
        <Menu.Item key="share" icon={<ShareAltOutlined />}>Share</Menu.Item>
        <Menu.Item key="transfer" icon={<SwapOutlined />}>Transfer Ownership</Menu.Item>
        <Menu.Item key="settings" icon={<SettingOutlined />}>Settings</Menu.Item>
        <Menu.Divider />
        <Menu.Item key="delete" icon={<DeleteOutlined />} danger>Delete</Menu.Item>
    </Menu>
);

// reusable download menu
const getDownloadMenu = (label: string) => (
    <Menu>
        <Menu.Item key="1" onClick={() => console.log(`Downloading complete data for ${label}...`)}>
            Download complete chart data
        </Menu.Item>
        <Menu.Item key="2" onClick={() => console.log(`Downloading underlying data for ${label}...`)}>
            Download Underlying data
        </Menu.Item>
        <Menu.Item key="3" onClick={() => console.log(`Downloading chart data for ${label}...`)}>
            Download Chart Data
        </Menu.Item>
    </Menu>
);

// Helper for Tooltip Content
const ChartTitleTooltip = ({ description, lastUpdated }: { description?: string, lastUpdated?: string }) => (
    <div>
        <div style={{ marginBottom: 4, fontWeight: 500 }}>{description || 'Key performance metric overview.'}</div>
        <div style={{ fontSize: 11, opacity: 0.8 }}>Last updated: {lastUpdated || 'Today, 10:00 AM'}</div>
    </div>
);

// --- Chart Container with Download Options ---
const ChartContainer = ({ title, children, description, lastUpdated }: { title: string, children: React.ReactNode, description?: string, lastUpdated?: string }) => {
    const [isHovered, setIsHovered] = useState(false);

    const titleNode = (
        <Tooltip title={<ChartTitleTooltip description={description} lastUpdated={lastUpdated} />}>
            <span style={{ cursor: 'help', borderBottom: '1px dashed #d9d9d9' }}>{title}</span>
        </Tooltip>
    );

    return (
        <Card
            title={titleNode}
            size="small"
            style={{ position: 'relative' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            extra={
                <Dropdown overlay={getDownloadMenu(title)} trigger={['click']}>
                    <DownloadOutlined
                        style={{
                            fontSize: 16,
                            color: palette.primary[2], // Light shade primary
                            opacity: isHovered ? 1 : 0,
                            transition: 'opacity 0.2s',
                            cursor: 'pointer'
                        }}
                    />
                </Dropdown>
            }
        >
            {children}
        </Card>
    );
};

// --- Custom Simple Components for "Dummy" Charts ---

// 1. Simple Horizontal Bar Chart
const SimpleBarChart = ({ data }: { data: { label: string, value: number, color?: string }[] }) => {
    const maxValue = Math.max(...data.map(d => d.value));
    return (
        <div style={{ padding: '10px 0' }}>
            {data.map((d, i) => (
                <div key={i} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Text style={{ fontSize: 12 }}>{d.label}</Text>
                        <Text strong style={{ fontSize: 12 }}>{d.value}%</Text>
                    </div>
                    <div style={{ height: 10, background: '#f5f5f5', borderRadius: 5, overflow: 'hidden' }}>
                        <div style={{
                            width: `${(d.value / maxValue) * 100}%`,
                            height: '100%',
                            background: d.color || colors.primary,
                            borderRadius: 5
                        }} />
                    </div>
                </div>
            ))}
        </div>
    );
};

// 2. Simple Column Chart
const SimpleColumnChart = ({ data }: { data: { label: string, value: number, color?: string }[] }) => {
    const maxValue = Math.max(...data.map(d => d.value));
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: 180, paddingBottom: 10 }}>
            {data.map((d, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                    <Tooltip title={`${d.label}: ${d.value}`}>
                        <div style={{
                            width: 30,
                            height: `${(d.value / maxValue) * 80}%`,
                            background: d.color || colors.primary,
                            borderRadius: '4px 4px 0 0',
                            transition: 'all 0.3s'
                        }} />
                    </Tooltip>
                    <Text style={{ fontSize: 10, marginTop: 8, color: '#8c8c8c' }}>{d.label}</Text>
                </div>
            ))}
        </div>
    );
};

// KPI Card Component
const KPICard = ({ title, value, subMetric, trend, type = 'default', description, lastUpdated }: any) => {
    const [isHovered, setIsHovered] = useState(false);

    let mainColor = '#262626';
    let trendColor = '#8c8c8c';

    if (type === 'danger') {
        mainColor = colors.error;
        trendColor = colors.error;
    } else if (trend === 'up') {
        trendColor = colors.success;
    } else if (trend === 'down') {
        trendColor = colors.error;
    }

    return (
        <Card
            bodyStyle={{ padding: '16px' }}
            hoverable
            style={{ height: '100%', borderRadius: 8 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                {/* Title with Hover Tooltip */}
                <Tooltip title={<ChartTitleTooltip description={description} lastUpdated={lastUpdated} />}>
                    <Text strong style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'help', borderBottom: '1px dashed #d9d9d9' }}>
                        {title}
                    </Text>
                </Tooltip>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {/* Download Icon for KPI */}
                    <Dropdown overlay={getDownloadMenu(title)} trigger={['click']}>
                        <DownloadOutlined
                            style={{
                                fontSize: 14,
                                color: palette.primary[2],
                                opacity: isHovered ? 1 : 0,
                                transition: 'opacity 0.2s',
                                cursor: 'pointer'
                            }}
                        />
                    </Dropdown>
                    {/* Removed InfoCircleOutlined as per request */}
                </div>
            </div>

            <Title level={2} style={{ margin: '8px 0', fontWeight: 600, color: mainColor }}>
                {value}
            </Title>

            {subMetric && (
                <div style={{ fontSize: 12, color: trendColor }}>
                    {trend === 'up' && <ArrowUpOutlined />} {trend === 'down' && <ArrowDownOutlined />}
                    <span style={{ marginLeft: 4 }}>{subMetric}</span>
                </div>
            )}
        </Card>
    );
};

const AnalyticsDashboardPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState('1');

    // Mock Data for Simple Charts
    const regionSalesData = [
        { label: 'North', value: 85, color: palette.primary[3] },
        { label: 'East', value: 65, color: palette.primary[2] },
        { label: 'South', value: 92, color: palette.primary[4] },
        { label: 'West', value: 45, color: palette.primary[1] },
    ];

    const trafficData = [
        { label: 'Direct', value: 40, color: colors.primary },
        { label: 'Social', value: 25, color: colors.success },
        { label: 'Referral', value: 15, color: colors.warning },
        { label: 'Organic', value: 35, color: palette.primary[2] },
    ];

    // 4. Table Data
    interface HubData {
        key: string;
        name: string;
        region: string;
        efficiency: number;
        status: string;
    }
    const tableColumns: ColumnsType<HubData> = [
        { title: 'Hub Name', dataIndex: 'name', key: 'name' },
        { title: 'Region', dataIndex: 'region', key: 'region' },
        {
            title: 'Efficiency',
            dataIndex: 'efficiency',
            key: 'efficiency',
            render: (val) => <Tag color={val > 90 ? colors.success : val > 80 ? colors.primary : colors.warning}>{val}%</Tag>
        },
        { title: 'Status', dataIndex: 'status', key: 'status' },
    ];
    const tableData: HubData[] = [
        { key: '1', name: 'Central Hub NY', region: 'NA', efficiency: 98, status: 'Active' },
        { key: '2', name: 'London Gateway', region: 'EU', efficiency: 95, status: 'Active' },
        { key: '3', name: 'Singapore Dist', region: 'AS', efficiency: 88, status: 'Warning' },
        { key: '4', name: 'Sao Paulo Hub', region: 'SA', efficiency: 92, status: 'Active' },
        { key: '5', name: 'Cape Town Ops', region: 'AF', efficiency: 75, status: 'Maintenance' },
    ];

    return (
        <div style={{ backgroundColor: '#f0f2f5', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

            {/* White Sub-header with Functional Tabs */}
            <div style={{ background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0' }}>
                <Tabs activeKey={activeTab} onChange={setActiveTab} tabBarStyle={{ marginBottom: 0 }}>
                    <Tabs.TabPane tab="Executive Overview" key="1" />
                    <Tabs.TabPane tab="Regional Performance" key="2" />
                    <Tabs.TabPane tab="Order Fulfilment" key="3" />
                </Tabs>
            </div>

            {/* Filter Bar */}
            <div style={{ background: '#fff', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <Space size="middle">
                    <FilterOutlined style={{ color: '#8c8c8c' }} />
                    <Select defaultValue="All Regions" size="small" style={{ minWidth: 130 }} bordered={false}>
                        <Option value="all">Global</Option>
                        <Option value="na">North America</Option>
                        <Option value="eu">Europe</Option>
                    </Select>
                    <Select defaultValue="This Quarter" size="small" style={{ minWidth: 110 }} bordered={false}>
                        <Option value="q1">Q1</Option>
                        <Option value="q2">Q2</Option>
                    </Select>
                </Space>
                <Space>
                    <Tooltip title="Export Report">
                        <Button size="small" icon={<CloudDownloadOutlined />} />
                    </Tooltip>
                    <Tooltip title="Download History">
                        <Button size="small" icon={<HistoryOutlined />} />
                    </Tooltip>
                    <Tooltip title="Full Screen">
                        <Button size="small" icon={<FullscreenOutlined />} />
                    </Tooltip>
                    <Dropdown overlay={actionMenu} trigger={['click']} placement="bottomRight">
                        <Button size="small" icon={<MoreOutlined />} />
                    </Dropdown>
                </Space>
            </div>

            {/* Blue Banner */}
            {activeTab === '1' && (
                <div style={{ background: colors.primary, padding: '8px 24px', color: '#fff', fontWeight: 600, textAlign: 'center', fontSize: 14 }}>
                    Real-time Enterprise Metrics
                </div>
            )}

            {/* Main Content Area */}
            <div style={{ padding: '16px' }}>
                {activeTab === '1' && (
                    <Row gutter={16}>
                        {/* LEFT COLUMN - METRICS GRID */}
                        <Col span={18}>
                            <Row gutter={[12, 12]}>
                                {/* Col 1: Financials */}
                                <Col span={4}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
                                        <KPICard title="Revenue (Today)" value="$42.5k" trend="up" subMetric="12% vs yest" description="Direct revenue from confirmed orders today." />
                                        <KPICard title="Gross Margin" value="38%" description="Total sales metric." />
                                        <KPICard title="Net Profit" value="$12.1k" />
                                        <KPICard title="OpEx Ratio" value="15%" />
                                        <KPICard title="Cash Flow" value="$8.2k" />
                                    </div>
                                </Col>

                                {/* Col 2: Sales */}
                                <Col span={4}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
                                        <KPICard title="Total Orders" value="1,240" />
                                        <KPICard title="Avg Order Value" value="$85" />
                                        <KPICard title="Conversion Rate" value="3.2%" trend="up" subMetric="+0.4%" />
                                        <KPICard title="Cart Abandon" value="65%" type="danger" />
                                        <KPICard title="Return Rate" value="2.1%" />
                                    </div>
                                </Col>

                                {/* Col 3: Fulfillment Speed */}
                                <Col span={4}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
                                        <KPICard title="Avg Deliv Time" value="2.1d" />
                                        <KPICard title="Dispatch Time" value="4.2h" />
                                        <KPICard title="Transit Time" value="1.8d" />
                                        <KPICard title="Last Mile" value="45m" />
                                        <KPICard title="On-Time %" value="98.5%" trend="up" subMetric="Best in Class" />
                                    </div>
                                </Col>

                                {/* Col 4: Issues (Red) */}
                                <Col span={4}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
                                        <KPICard title="Late Deliveries" value="15" type="danger" />
                                        <KPICard title="Lost Packages" value="2" type="danger" />
                                        <KPICard title="Damaged Items" value="8" type="danger" />
                                        <KPICard title="Stockouts" value="4" type="danger" />
                                        <KPICard title="SLA Breaches" value="12" type="danger" />
                                    </div>
                                </Col>

                                {/* Col 5: Customer (Scores) */}
                                <Col span={4}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
                                        <KPICard title="NPS Score" value="72" trend="up" subMetric="+2" />
                                        <KPICard title="CSAT Score" value="4.8" />
                                        <KPICard title="Support Tix" value="142" />
                                        <KPICard title="Resol. Time" value="2.5h" />
                                        <KPICard title="Churn Rate" value="1.2%" />
                                    </div>
                                </Col>

                                {/* Col 6: Efficiency */}
                                <Col span={4}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
                                        <KPICard title="Whs Utiliz." value="85%" />
                                        <KPICard title="Inventory Turns" value="12" />
                                        <KPICard title="Labor Efficiency" value="92%" />
                                        <KPICard title="Fleet Uptime" value="99%" />
                                        <KPICard title="Fuel Cost" value="-5%" trend="up" subMetric="Savings" />
                                    </div>
                                </Col>
                            </Row>
                        </Col>

                        {/* RIGHT COLUMN - Simple Custom Charts */}
                        <Col span={6}>
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                {/* Simple Bar Chart with Download */}
                                <ChartContainer title="Regional Sales Performance" description="Sales distribution across 4 main global regions.">
                                    <SimpleBarChart data={regionSalesData} />
                                </ChartContainer>

                                {/* Simple Column Chart with Download */}
                                <ChartContainer title="Traffic Sources" description="Breakdown of inbound web traffic by source.">
                                    <SimpleColumnChart data={trafficData} />
                                </ChartContainer>
                            </Space>
                        </Col>
                    </Row>
                )}

                {activeTab === '2' && (
                    <Row gutter={16}>
                        <Col span={12}>
                            {/* Reusing Simple Charts for Demo */}
                            <ChartContainer title="Traffic Distribution">
                                <SimpleColumnChart data={trafficData} />
                            </ChartContainer>
                        </Col>
                        <Col span={12}>
                            <Card title="Top Performing Hubs" style={{ height: '100%' }}>
                                <Table
                                    columns={tableColumns}
                                    dataSource={tableData}
                                    pagination={false}
                                    size="small"
                                />
                            </Card>
                        </Col>
                    </Row>
                )}

                {activeTab === '3' && (
                    <div style={{ textAlign: 'center', padding: 50 }}>
                        <Title level={4}>Order Fulfilment Details</Title>
                        <Text type="secondary">Detailed timeline and logistics data would go here.</Text>
                        <div style={{ marginTop: 24 }}>
                            <ChartContainer title="Projected Delivery Volume">
                                <SimpleBarChart data={regionSalesData} />
                            </ChartContainer>
                        </div>
                    </div>
                )}

            </div>

            {/* Floating Action Button Removed */}
        </div>
    );
};

export default AnalyticsDashboardPage;
